"use client"

import { useEffect, useState } from "react"
import { getWithTokenAPI } from "../../utils/Api"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import {
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Pagination,
  Stack,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  LocalOffer as LocalOfferIcon,
  HourglassEmpty as PendingIcon,
  CheckCircleOutline as DeliveredIcon,
  LocalShipping as ShippedIcon,
  RotateRight as ProcessingIcon,
  Cancel as CancelledIcon,
  Store as StoreIcon,
  LocalAtm as LocalAtmIcon,
  LocationOn as LocationPinIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  LocalShipping as LocalShippingIcon,
  AccessTime as AccessTimeIcon,
  ShoppingCart as ShoppingCartIcon,
  Inventory as InventoryIcon,
  MonetizationOn as MonetizationOnIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useTheme, useMediaQuery } from '@mui/material';

const OrderManagement = () => {
  const [orders, setOrders] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  console.log(selectedOrder)
  const [openOrderDialog, setOpenOrderDialog] = useState(false)
  const [currentSellerInCard, setCurrentSellerInCard] = useState(null)
  console.log("currentSellerInCard", currentSellerInCard)
  const [activeStatus, setActiveStatus] = useState("Pending")
  const [loading, setLoading] = useState(true)
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [ordersPerPage] = useState(10)
  const navigate = useNavigate()
  const token = localStorage.getItem("token")
  const primaryGreen = "#DC3545"
  const lightGreen = "#ffe5e5"
  const darkGreen = "#b02a37"
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('xxl'));

  useEffect(() => {
    const fetchOrderData = async () => {
      setLoading(true)
      try {
        const res = await getWithTokenAPI(token, "/order/find-all")
        if (res.statusCode === 200) {
          const data = res.data.orders.filter((cart) => cart.cart_id.cart.length > 0);
          setOrders(data)

        } else {
          toast.error(res.message || "Failed to load orders.")
          setOrders([])
        }
      } catch (error) {
        console.error("Fetch orders error:", error)
        toast.error("Failed to load orders due to a network error.")
        setOrders([])
      } finally {
        setLoading(false)
      }
    }

    fetchOrderData()
  }, [token])

  const filteredOrders = orders.filter((element) => element.order_status === activeStatus)

  // Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder)
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage)

  const handlePageChange = (event, value) => {
    setCurrentPage(value)
  }

  const handleViewOrder = (order) => {
    setSelectedOrder(order);

    // Get the first cart item's seller details
    const firstCartItem = order.cart_id?.cart?.[0] || null;
    const firstSeller = {
      sellerId: firstCartItem?.seller_id?._id || null,
      sellerName: firstCartItem?.seller_id?.name || null, // "Ibrahim Konate"
      discountPrice: firstCartItem?.discount_price || null, // 40
      orderStatus: firstCartItem?.order_status || null, // "Pending"
    };

    setCurrentSellerInCard(firstSeller);

    const allSellerNames = order.cart_id?.cart
      ?.map((item) => item.seller_id?.name)
      ?.filter(Boolean) // Remove any undefined/null values
      ?.join(", ") || "No sellers found";


    setCurrentSellerInCard({ _id: firstSeller._id, name: allSellerNames });

    setOpenOrderDialog(true);
  };

  const handleSellerNameClick = (order, sellerDetails) => {
    setSelectedOrder(order)
    setCurrentSellerInCard(sellerDetails)
    setOpenOrderDialog(true)
  }

  const handleStatusChange = (status) => {
    setActiveStatus(status)
    setCurrentPage(1)
  }

  const getStatusChipProps = (status) => {
    switch (status) {
      case "Pending":
        return {
          label: "Pending",
          sx: { backgroundColor: "#fff3e0", color: "#e65100" },
          icon: <PendingIcon />,
        }
      case "Processing":
        return {
          label: "Processing",
          sx: { backgroundColor: "#e0f2f7", color: "#01579b" },
          icon: <ProcessingIcon />,
        }
      case "Shipped":
        return {
          label: "Shipped",
          sx: { backgroundColor: "#e3f2fd", color: "#1976d2" },
          icon: <ShippedIcon />,
        }
      case "Delivered":
        return {
          label: "Delivered",
          sx: { backgroundColor: lightGreen, color: darkGreen },
          icon: <DeliveredIcon />,
        }
      case "Cancelled":
        return {
          label: "Cancelled",
          sx: { backgroundColor: "#ffebee", color: "#c62828" },
          icon: <CancelledIcon />,
        }
      default:
        return {
          label: status,
          sx: { backgroundColor: "#f5f5f5", color: "#424242" },
        }
    }
  }

  const getPaymentStatusChipProps = (status) => {
    switch (status) {
      case "Paid":
        return {
          label: "Paid",
          sx: { backgroundColor: lightGreen, color: darkGreen },
          icon: <CheckCircleIcon fontSize="small" />,
        }
      case "Failed":
        return {
          label: "Failed",
          sx: { backgroundColor: "#ffebee", color: "#c62828" },
          icon: <ErrorIcon fontSize="small" />,
        }
      case "Pending":
        return {
          label: "Pending",
          sx: { backgroundColor: "#fff3e0", color: "#e65100" },
          icon: <PendingIcon fontSize="small" />,
        }
      default:
        return {
          label: status || "N/A",
          sx: { backgroundColor: "#f5f5f5", color: "#424242" },
        }
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0)
  }

  // const formatCurrency = (amount) => {
  //   return new Intl.NumberFormat("en-KE", {
  //     style: "currency",
  //     currency: "Ksh",
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2,
  //   }).format(amount || 0);
  // };

  const calculateSubtotal = (order) => {
    console.log(order, "orderssss");
    
    if (!order?.cart_id) return 0
    return (order.cart_id.total_Price || 0) - (order.cart_id.tax || 0)
  }

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <ShoppingBagIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: primaryGreen, fontWeight: "bold" }}>
            Order Management
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage customer orders and track their status
          </Typography>
        </Box>
      </Box>

      {/* Status Filter Buttons */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          backgroundColor: lightGreen,
          justifyContent: "space-evenly",
          width: "max-content",
          padding: "5px",
          borderRadius: "5px",
        }}
      >
        {["Pending", "Processing", "Shipped", "Delivered", "Cancelled"].map((status) => (
          <Button
            key={status}
            variant={activeStatus === status ? "contained" : "outlined"}
            onClick={() => handleStatusChange(status)}
            sx={{
              px: 3,
              py: 1,
              textTransform: "capitalize",
              backgroundColor: activeStatus === status ? primaryGreen : "white",
              color: activeStatus === status ? "white" : primaryGreen,
              borderColor: "white",
              fontWeight: "bold",
            }}
          >
            {status}
          </Button>
        ))}
      </Box>

      {/* Main Content Table */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress sx={{ color: primaryGreen }} />
          <Typography sx={{ ml: 2, color: primaryGreen }}>Loading Orders...</Typography>
        </Box>
      ) : orders.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            textAlign: "center",
          }}
        >
          <ShoppingBagIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: "text.disabled" }}>
            No orders found
          </Typography>
        </Box>
      ) : filteredOrders.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 3, border: "1px solid #e0e0e0" }}>
            <Table sx={{ minWidth: 650 }} aria-label="order table">
              <TableHead>
                <TableRow sx={{ backgroundColor: lightGreen }}>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Seller Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Requested By</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Subtotal(Ksh)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Tax(Ksh)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Total(Ksh)</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }} align="center">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentOrders.map((element, index) => {
                  const statusProps = getStatusChipProps(element.order_status)
                  const uniqueSellerIds = {}
                  element.cart_id?.cart?.forEach((item) => {
                    if (item.seller_id && !uniqueSellerIds[item.seller_id._id]) {
                      uniqueSellerIds[item.seller_id._id] = item.seller_id
                    }
                  })
                  const uniqueSellers = Object.values(uniqueSellerIds)
                  return (
                    <TableRow
                      key={element._id}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <TableCell>{indexOfFirstOrder + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            src={element.cart_id?.cart?.[0]?.product_id?.product_image?.[0] || "#"}
                            variant="rounded"
                            sx={{ width: 40, height: 40, mr: 1 }}
                          >
                            {element.cart_id?.cart?.[0]?.product_id?.name?.charAt(0) || "P"}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {element.cart_id?.cart?.[0]?.product_id?.name || "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {uniqueSellers.length > 0 ? (
                          uniqueSellers.map((seller, idx) => (
                            <Typography
                              key={seller._id}
                              variant="body2"
                              sx={{
                                cursor: "pointer",
                                textDecoration: "underline",
                                color: primaryGreen,
                                fontWeight: "bold",
                                display: "inline-block",
                                "&:not(:last-child)": {
                                  marginRight: "4px",
                                },
                              }}
                              onClick={() => navigate(`/backoffice/seller-details/${seller._id}`)}
                            >
                              {seller.name}
                              {idx < uniqueSellers.length - 1 && ","}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2">N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{
                              bgcolor: primaryGreen,
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: 14,
                            }}
                          >
                            {element.buyer_id?.name?.charAt(0) || "B"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {element.buyer_id?.name || "-"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {element.buyer_id?.email || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        
                        {element.cart_id
                          ? (element.cart_id.total_Price - (element.cart_id.tax || 0)).toFixed(2)
                          : "0.00"}
                      </TableCell>
                      <TableCell>{element.cart_id?.tax?.toFixed(2) || "0.00"}</TableCell>
                      <TableCell>{element.cart_id?.total_Price?.toFixed(2) || "0.00"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarTodayIcon
                            sx={{
                              fontSize: 16,
                              mr: 0.5,
                              color: "text.secondary",
                            }}
                          />
                          {new Date(element.created_at).toLocaleDateString()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusProps.label}
                          size="small"
                          sx={{
                            ...statusProps.sx,
                            "& .MuiChip-icon": { fontSize: 18 },
                          }}
                          icon={statusProps.icon}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleViewOrder(element)}
                          sx={{ color: primaryGreen, mr: 1 }}
                          aria-label="view order details"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              mt: 3,
              pb: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="green"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: primaryGreen,
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: primaryGreen,
                  color: "white",
                },
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            textAlign: "center",
          }}
        >
          <ShoppingBagIcon sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No {activeStatus} orders available
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            There are currently no orders with the '{activeStatus}' status.
          </Typography>
        </Box>
      )}

      {/* Order Detail Dialog */}
      <Dialog
        open={openOrderDialog}
        onClose={() => {
          setOpenOrderDialog(false);
          setCurrentSellerInCard(null);
        }}
        fullWidth
        fullScreen  // This makes the dialog full screen
        sx={{
          "& .MuiDialog-paper": {
            m: 0,  // Remove margin
            width: "100%",  // Full width
            maxWidth: "100%",  // Override maxWidth
          }
        }}
      >
        {selectedOrder && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: primaryGreen,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div>
                <ReceiptIcon sx={{ mr: 1 }} />
                Order Details
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setOpenOrderDialog(false)}
              ></button>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              {/* Row 1: Seller Details Card (full width) */}
              {/* <Box sx={{ mb: 2 }}>
          {" "}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              width: "100%", 
              border: `1px solid ${lightGreen}`,
              borderRadius: 2,
              backgroundColor: "#f9f9f9",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                color: darkGreen,
                display: "flex",
                alignItems: "center",
                borderBottom: `1px solid ${lightGreen}`,
                pb: 1,
              }}
            >
              <StoreIcon sx={{ mr: 1 }} />
              Seller Details
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <Avatar
                src={currentSellerInCard?.profile_image || ""}
                sx={{ width: 40, height: 40, mr: 1 }}
              >
                {currentSellerInCard?.name?.charAt(0) || "S"}
              </Avatar>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {currentSellerInCard?.name || "N/A"}
              </Typography>
            </Box>
          </Paper>
        </Box> */}

              {/* Row 2: Buyer Information and Order Summary Cards (side-by-side) */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2, // Spacing between cards
                  justifyContent: "space-between",
                  alignItems: "stretch", // Ensures these two cards have equal height
                  flexWrap: "wrap", // Allows wrapping if screen is too small, though maxWidth="md" usually prevents this
                }}
              >
                {/* Buyer Information Card - Takes half width */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: "1 1 calc(50% - 8px)", // Occupies roughly 50% minus half the gap
                    border: `1px solid ${lightGreen}`,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: darkGreen,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    Buyer Information
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      src={selectedOrder.buyer_id?.profile_image}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    >
                      {selectedOrder.buyer_id?.name?.charAt(0) || "B"}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {selectedOrder.buyer_id?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        ID: {selectedOrder.buyer_id?._id || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      pl: 2,
                      py: 2,
                      border: "1px solid #ccc",
                      borderRadius: 2,
                      backgroundColor: "#f9f9f9",
                      flexGrow: 1, // Allows this section to grow and fill available space
                    }}
                  >
                    {/* Address */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <LocationPinIcon sx={{ color: darkGreen, mr: 1 }} />
                        <Typography variant="body2">Address</Typography>
                      </Box>
                      <Box sx={{ marginLeft: "25px" }}>
                        <Typography variant="body2">
                          {selectedOrder.address_id?.street || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.address_id?.city || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.address_id?.state || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.address_id?.country || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          {selectedOrder.address_id?.zip_code || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Email */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <EmailIcon sx={{ color: darkGreen, mr: 1 }} />
                      <Typography variant="body2">
                        {selectedOrder.buyer_id?.email || "N/A"}
                      </Typography>
                    </Box>
                    {/* Phone */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PhoneIcon sx={{ color: darkGreen, mr: 1 }} />
                      <Typography variant="body2">
                        {selectedOrder.buyer_id?.phone_number || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Order Summary Card - Takes half width */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: "1 1 calc(50% - 8px)", // Occupies roughly 50% minus half the gap
                    border: `1px solid ${lightGreen}`,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: darkGreen,
                      display: "flex",
                      alignItems: "center",
                      borderBottom: `1px solid ${lightGreen}`,
                      pb: 1,
                    }}
                  >
                    <ReceiptIcon sx={{ mr: 1 }} />
                    Order Summary
                  </Typography>

                  {/* Order Info Section - Enhanced with Icons */}
                  <Box sx={{ mb: 3 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ReceiptIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Order ID:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {selectedOrder.order_id ||
                            selectedOrder._id?.substring(0, 8) ||
                            "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarTodayIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Order Date:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {formatDate(selectedOrder.created_at)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <InventoryIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Total Products:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {selectedOrder.cart_id?.cart?.length || 0} items
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PaymentIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Payment Method:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "500", textTransform: "capitalize" }}
                        >
                          {selectedOrder.payment_method || "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LocalShippingIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Shipping:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "500", textTransform: "capitalize" }}
                        >
                          {selectedOrder.shipping || "Standard"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AccessTimeIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Order Status:
                        </Typography>
                        <Chip
                          label={getStatusChipProps(selectedOrder.order_status).label}
                          size="small"
                          sx={{
                            ...getStatusChipProps(selectedOrder.order_status).sx,
                            "& .MuiChip-icon": { fontSize: 16 },
                            fontWeight: 500,
                          }}
                          icon={getStatusChipProps(selectedOrder.order_status).icon}
                        />
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <MonetizationOnIcon
                          sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", width: "120px" }}
                        >
                          Order Payment:
                        </Typography>
                        <Chip
                          label={
                            getPaymentStatusChipProps(selectedOrder.cart_id?.payment_status)
                              .label
                          }
                          size="small"
                          sx={{
                            ...getPaymentStatusChipProps(selectedOrder.cart_id?.payment_status)
                              .sx,
                            "& .MuiChip-icon": { fontSize: 16 },
                            fontWeight: 500,
                          }}
                          icon={
                            getPaymentStatusChipProps(selectedOrder.cart_id?.payment_status)
                              .icon
                          }
                        />
                      </Box>


                      {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ShoppingCartIcon
                    sx={{ color: primaryGreen, mr: 1, fontSize: "1.2rem" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", width: "120px" }}
                  >
                    Cart Payment:
                  </Typography>
                  <Chip
                    label={
                      getPaymentStatusChipProps(
                        selectedOrder.cart_id?.payment_status
                      ).label
                    }
                    size="small"
                    sx={{
                      ...getPaymentStatusChipProps(
                        selectedOrder.cart_id?.payment_status
                      ).sx,
                      "& .MuiChip-icon": { fontSize: 16 },
                      fontWeight: 500,
                    }}
                    icon={
                      getPaymentStatusChipProps(
                        selectedOrder.cart_id?.payment_status
                      ).icon
                    }
                  />
                </Box> */}
                    </Stack>
                  </Box>

                  {/* Payment Details Section */}
                  <Box sx={{ mt: "auto" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                        borderBottom: `1px solid ${lightGreen}`,
                        pb: 1,
                      }}
                    >
                      <LocalAtmIcon sx={{ mr: 1, fontSize: 20 }} />
                      Payment Details
                    </Typography>

                    <Box
                      sx={{
                        backgroundColor: "#f9f9f9",
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Subtotal(Ksh):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(calculateSubtotal(selectedOrder))}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Tax(Ksh):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(selectedOrder.cart_id?.tax || 0)}
                        </Typography>
                      </Box>

                      {selectedOrder.shipping_cost > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">Shipping(Ksh):</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(selectedOrder.shipping_cost || 0)}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                          Total Amount(Ksh):
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            color: darkGreen,
                          }}
                        >
                          {(
                            selectedOrder.cart_id?.total_Price ||
                            selectedOrder.total_price ||
                            0
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Products in Order */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: darkGreen,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <LocalOfferIcon sx={{ mr: 1 }} />
                  Products in Order
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{ border: `1px solid ${lightGreen}`}}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: lightGreen }}>
                        <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Price(Ksh)</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Seller</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                          Total(Ksh)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.cart_id?.cart?.map((item, prodIndex) => (
                        <TableRow key={prodIndex}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar
                                src={item.product_id?.product_image?.[0] || "#"}
                                variant="rounded"
                                sx={{ width: 40, height: 40, mr: 1 }}
                              >
                                {item.product_id?.name?.charAt(0) || "P"}
                              </Avatar>
                              <Typography variant="body2">
                                {item.product_id?.name || "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            {item.discount_price?.toFixed(2) || "0.00"}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                cursor: "pointer",
                                textDecoration: "underline",
                                color: primaryGreen,
                                fontWeight: "bold",
                              }}
                              onClick={() =>
                                navigate(
                                  `/backoffice/seller-details/${item.seller_id._id}`
                                )
                              }
                            >
                              {item.seller_id?.name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            
                            {(item.quantity * item.discount_price)?.toFixed(2) ||
                              "0.00"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => {
                  setOpenOrderDialog(false);
                  setCurrentSellerInCard(null);
                }}
                sx={{
                  color: primaryGreen,
                  borderColor: primaryGreen,
                  "&:hover": {
                    backgroundColor: lightGreen,
                    borderColor: darkGreen,
                  },
                }}
                variant="outlined"
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default OrderManagement
